/**
 * 매장 상세 패널 — 탭 구성
 *
 *  매장 목록(마스터-디테일) 우측 패널과 /business/:companySeq 단독 페이지에서 공용.
 *  섹션을 탭으로 분리: 기본정보 / 운영정보 / 정산정보 / 구성원
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  businessApi,
  companyApi,
  type AdminCompanyTicketDto,
  type CompanyMemberDto,
  type CompanyPromoPhotoDto,
  type UpdateCompanyInfoPayload,
} from '@/services/api/members';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  Section,
  DescList,
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/common';
import { DataGrid, makeActionCell, makeBadgeCell } from '@/components/grid';
import { CompanyCreditPanel } from '@/pages/credit';
import { CompanyStoragePanel } from './CompanyStoragePanel';
import { CompanyGuardiansPanel } from './CompanyGuardiansPanel';

const ACTIVATION_TEXT: Record<string, string> = {
  NOT_REQUESTED: '미요청',
  REQUESTED: '활성화 요청',
  AGREED: '승인',
  DENIED: '반려',
};

/**
 * 활성화 상태 뱃지 노출 여부.
 * 이미 '활성'인 매장에서 '승인'은 활성 뱃지와 중복이고, '미요청'(관리자가 요청 없이 직접 켠 경우)은
 * 활성 상태와 어긋나 보여 혼란만 준다 → 둘 다 감춘다.
 * 반면 '요청'(승인 대기)·'반려'는 활성 매장이라도 후속 조치가 남은 정보이므로 그대로 노출한다.
 */
export const shouldShowActivationBadge = (
  activeYn?: string | null,
  status?: string | null
): boolean => activeYn !== 'Y' || status === 'REQUESTED' || status === 'DENIED';

const PROVIDE_LABEL: Record<string, string> = {
  PROVIDED_KINDERGARTEN: '유치원',
  PROVIDED_DAYCARE: '데이케어',
  PROVIDED_HOTEL: '호텔링',
  PROVIDED_AGILITY: '어질리티',
  PROVIDED_BEHAVIOR_CORRECTION: '행동교정',
  PROVIDED_PROFESSIONAL_TRAINING: '전문훈련',
  PROVIDED_GROOMING: '미용',
  PROVIDED_BATH: '목욕',
  PROVIDED_SELF_BATH: '셀프목욕',
};

const FACILITY_LABEL: Record<string, string> = {
  FACILITY_TYPE_SUB_YARD: '보조운동장',
  FACILITY_TYPE_ISOLATION: '격리시설',
  FACILITY_TYPE_KENNEL: '켄넬',
  FACILITY_TYPE_WEIGHT_SEPARATION: '체급분리시설',
  FACILITY_TYPE_CCTV: 'CCTV',
};

const DAY_OF_WEEK_LABEL: Record<string, string> = {
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
  SUNDAY: '일',
};

type TabKey =
  | 'basic'
  | 'operation'
  | 'tickets'
  | 'settlement'
  | 'members'
  | 'guardians'
  | 'credit'
  | 'storage';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'basic', label: '기본정보' },
  { key: 'operation', label: '운영정보' },
  { key: 'tickets', label: '이용권' },
  { key: 'settlement', label: '정산정보' },
  { key: 'members', label: '구성원' },
  { key: 'guardians', label: '이용보호자' },
  { key: 'credit', label: '크레딧' },
  { key: 'storage', label: '저장용량' },
];

// Daum 우편번호 검색 SDK 로더 (키 불필요). 매장 정보 수정 모달의 주소 검색에 사용.
let daumPostcodePromise: Promise<void> | null = null;
function loadDaumPostcode(): Promise<void> {
  if ((window as any).daum?.Postcode) return Promise.resolve();
  if (daumPostcodePromise) return daumPostcodePromise;
  daumPostcodePromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src =
      'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      daumPostcodePromise = null;
      reject(new Error('주소 검색 SDK 로드 실패'));
    };
    document.head.appendChild(s);
  });
  return daumPostcodePromise;
}

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

/**
 * 매장 이용권 상품 행 — 1건당 2줄로 압축 (탭 스크롤 최소화).
 *  1줄: 상품명 + 판매중/중지 + 판매가(할인) + 횟수/박수
 *  2줄: 상품유형 · 유효기간 정책 · 최소 구매/이용 수량 · 발급 건수/누적 결제액 · 최근 발급일
 */
function CompanyTicketRow({ ticket }: { ticket: AdminCompanyTicketDto }) {
  const price = (() => {
    const amount = Number(ticket.amount ?? 0).toLocaleString();
    if (ticket.discount && ticket.discount > 0 && ticket.cost) {
      return `${amount}원 (정가 ${Number(ticket.cost).toLocaleString()} · ${ticket.discount}%↓)`;
    }
    return `${amount}원`;
  })();

  // 호텔은 박수, 나머지는 횟수. 다회권이 아니면 표기 생략
  const qty = (() => {
    if (!ticket.splitCount || ticket.splitCount <= 1) return null;
    return ticket.cdProductType === 'PRODUCT_TYPE_HOTEL'
      ? `${ticket.splitCount}박`
      : `${ticket.splitCount}회`;
  })();

  // 유효기간 정책: durationDays(구매일 기준) 우선, 없으면 고정 기간
  const validity = (() => {
    if (ticket.durationDays) return `구매일+${ticket.durationDays}일`;
    if (ticket.dtUsable || ticket.dtExpired) {
      return `${ticket.dtUsable ?? '?'} ~ ${ticket.dtExpired ?? '?'}`;
    }
    return '유효기간 미설정';
  })();

  const meta = [
    PRODUCT_TYPE_LABEL[ticket.cdProductType] ?? ticket.cdProductType,
    validity,
    ticket.minPurchaseQty ? `최소구매 ${ticket.minPurchaseQty}` : null,
    ticket.minUseQty ? `최소이용 ${ticket.minUseQty}` : null,
    `발급 ${Number(ticket.issuedCount ?? 0).toLocaleString()}건`,
    `누적 ${Number(ticket.salesAmount ?? 0).toLocaleString()}원`,
    ticket.lastIssuedAt ? `최근 ${ticket.lastIssuedAt.slice(0, 10)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">
            {ticket.name}
          </span>
          <StatusBadge tone={ticket.activeYn === 'Y' ? 'success' : 'neutral'}>
            {ticket.activeYn === 'Y' ? '판매중' : '중지'}
          </StatusBadge>
          <span className="shrink-0 text-[11px] text-gray-600">{price}</span>
          {qty && (
            <span className="shrink-0 text-[11px] text-gray-500">{qty}</span>
          )}
        </div>
        <span className="shrink-0 text-[10px] text-gray-400">
          SEQ {ticket.productTicketSeq}
        </span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
        {meta}
      </div>
    </div>
  );
}

function BadgeChips({
  items,
  emptyMessage,
}: {
  items: { key: string; label: string; active: boolean }[];
  emptyMessage: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-400">{emptyMessage}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(item => (
        <StatusBadge key={item.key} tone={item.active ? 'success' : 'neutral'}>
          {item.label}
        </StatusBadge>
      ))}
    </div>
  );
}

const formatDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('ko-KR') : '-';

const formatDateTime = (s?: string | null) =>
  s
    ? new Date(s).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

interface BusinessDetailPanelProps {
  companySeq: number;
  /** 우측 패널로 쓸 때 닫기 버튼 노출 (단독 페이지에서는 생략) */
  onClose?: () => void;
}

/**
 * 선택된 매장의 상세 — 헤더(요약 + 활성화 액션) + 탭 본문
 * 컨테이너는 h-full 을 채우고 본문만 내부 스크롤한다.
 */
export function BusinessDetailPanel({
  companySeq,
  onClose,
}: BusinessDetailPanelProps) {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>('basic');
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDenyModalOpen, setIsDenyModalOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [deniedReason, setDeniedReason] = useState('');
  /** 요청과 무관한 직접 활성화/비활성화 확인 모달 — 'Y' 활성화, 'N' 비활성화 */
  const [directTarget, setDirectTarget] = useState<'Y' | 'N' | null>(null);
  const [notifyOnActivate, setNotifyOnActivate] = useState(true);
  const [actionMessage, setActionMessage] = useState('');
  const [pinResetTarget, setPinResetTarget] = useState<CompanyMemberDto | null>(
    null
  );

  // 매장이 바뀌면 첫 탭으로 되돌린다
  useEffect(() => {
    setTab('basic');
  }, [companySeq]);

  // 토스트 자동 해제
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(''), 3000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  const {
    data: company,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['companyDetail', String(companySeq)],
    queryFn: () => companyApi.getCompanyDetail(companySeq),
    enabled: !!companySeq,
  });

  const ticketsQuery = useQuery({
    queryKey: ['companyTickets', String(companySeq)],
    queryFn: () => companyApi.getCompanyTickets(companySeq),
    enabled: !!companySeq,
  });

  /** 판매중(ACTIVE_YN=Y) 상품을 위로, 그 안에서는 노출 순서 그대로 */
  const tickets = useMemo(() => {
    const list = [...(ticketsQuery.data ?? [])];
    return list.sort((a, b) => {
      const rankA = a.activeYn === 'Y' ? 0 : 1;
      const rankB = b.activeYn === 'Y' ? 0 : 1;
      if (rankA !== rankB) return rankA - rankB;
      return (a.dataOrder ?? 0) - (b.dataOrder ?? 0);
    });
  }, [ticketsQuery.data]);

  const activeTicketCount = tickets.filter(t => t.activeYn === 'Y').length;

  const updateActiveMutation = useMutation({
    mutationFn: ({
      activeYn,
      deniedReason,
    }: {
      activeYn: 'Y' | 'N';
      deniedReason?: string;
    }) =>
      businessApi.updateBusinessCompanyActive({
        companySeq,
        activeYn,
        deniedReason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setIsApproveModalOpen(false);
      setIsDenyModalOpen(false);
      setDeniedReason('');
      setActionMessage('활성화 상태가 변경되었습니다.');
    },
    onError: () => setActionMessage('활성화 상태 변경에 실패했습니다.'),
  });

  // 활성화 요청 없이 관리자가 직접 켜고 끄는 경로 (승인/반려와 별도 API)
  const activationStateMutation = useMutation({
    mutationFn: ({
      activeYn,
      notify,
    }: {
      activeYn: 'Y' | 'N';
      notify: boolean;
    }) =>
      businessApi.updateCompanyActivationState({
        companySeq,
        activeYn,
        notifyYn: notify ? 'Y' : 'N',
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setDirectTarget(null);
      setActionMessage(
        variables.activeYn === 'Y'
          ? '매장을 활성화했습니다.'
          : '매장을 비활성화했습니다.'
      );
    },
    onError: () => setActionMessage('활성화 상태 변경에 실패했습니다.'),
  });

  const resetPinMutation = useMutation({
    mutationFn: (bizUserSeq: number) => businessApi.resetBizSealPin(bizUserSeq),
    onSuccess: () => {
      setPinResetTarget(null);
      setActionMessage(
        '도장 PIN이 초기화되었습니다. 사업자가 새 PIN을 등록해야 합니다.'
      );
    },
    onError: () => setActionMessage('도장 PIN 초기화에 실패했습니다.'),
  });

  // 매장 홍보사진 — 대표 지정 / 삭제
  const [photoDeleteTarget, setPhotoDeleteTarget] =
    useState<CompanyPromoPhotoDto | null>(null);
  const representPhotoMutation = useMutation({
    mutationFn: (companyPhotoSeq: number) =>
      companyApi.setRepresentativeCompanyPhoto(companySeq, companyPhotoSeq),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setActionMessage('대표 사진으로 지정했습니다.');
    },
    onError: () => setActionMessage('대표 사진 지정에 실패했습니다.'),
  });
  const deletePhotoMutation = useMutation({
    mutationFn: (companyPhotoSeq: number) =>
      companyApi.deleteCompanyPhoto(companySeq, companyPhotoSeq),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setPhotoDeleteTarget(null);
      setActionMessage('홍보사진을 삭제했습니다.');
    },
    onError: () => setActionMessage('홍보사진 삭제에 실패했습니다.'),
  });

  // 홍보사진 다운로드 — CDN에서 blob 으로 받아 저장(실패 시 새 탭)
  const downloadPhoto = async (photo: CompanyPromoPhotoDto) => {
    const url = photo.fileUrl;
    if (!url) return;
    const ext = (
      url.split('?')[0].match(/\.([a-zA-Z0-9]+)$/)?.[1] ?? 'jpg'
    ).toLowerCase();
    const name = `company${companySeq}_photo${photo.companyPhotoSeq}.${ext}`;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  };

  // 매장 정보 수정 — 섹션별(기본정보/정산/운영) 편집
  const [editSection, setEditSection] = useState<
    'basic' | 'settlement' | 'operation' | null
  >(null);
  const [editForm, setEditForm] = useState({
    mainTelNumber: '',
    zipcode: '',
    companyAddr: '',
    companyAddrDetail: '',
    region: '',
    playgroundYn: 'N',
    maxWeight: '',
    duplicateReservYn: 'N',
    reservCapacityLimitYn: 'N',
    dailyReservCapacity: '',
    mainBank: '',
    mainAccountNumber: '',
    depositor: '',
    kakaoAlimUseYn: 'N',
    kakaoAlimFallbackSmsYn: 'N',
    remainTicketIssueQty: '',
  });
  const openEdit = (section: 'basic' | 'settlement' | 'operation') => {
    if (!company) return;
    setEditForm({
      mainTelNumber: company.mainTelNumber ?? '',
      zipcode: company.zipcode ?? '',
      companyAddr: company.companyAddr ?? '',
      companyAddrDetail: company.companyAddrDetail ?? '',
      region: company.region ?? '',
      playgroundYn: company.playgroundYn ?? 'N',
      maxWeight: company.maxWeight != null ? String(company.maxWeight) : '',
      duplicateReservYn: company.duplicateReservYn ?? 'N',
      reservCapacityLimitYn: company.reservCapacityLimitYn ?? 'N',
      dailyReservCapacity:
        company.dailyReservCapacity != null
          ? String(company.dailyReservCapacity)
          : '',
      mainBank: company.mainBank ?? '',
      mainAccountNumber: company.mainAccountNumber ?? '',
      depositor: company.depositor ?? '',
      kakaoAlimUseYn: company.kakaoAlimUseYn ?? 'N',
      kakaoAlimFallbackSmsYn: company.kakaoAlimFallbackSmsYn ?? 'N',
      remainTicketIssueQty:
        company.remainTicketIssueQty != null
          ? String(company.remainTicketIssueQty)
          : '',
    });
    setEditSection(section);
  };
  const openAddressSearch = async () => {
    try {
      await loadDaumPostcode();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      new (window as any).daum.Postcode({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        oncomplete: (data: any) => {
          const addr =
            data.userSelectedType === 'R'
              ? data.roadAddress
              : data.jibunAddress;
          const region = [data.sido, data.sigungu].filter(Boolean).join(' ');
          setEditForm(prev => ({
            ...prev,
            companyAddr: addr,
            zipcode: data.zonecode ?? prev.zipcode,
            region: region || prev.region,
          }));
        },
      }).open();
    } catch {
      setActionMessage('주소 검색을 불러오지 못했습니다.');
    }
  };
  const updateInfoMutation = useMutation({
    mutationFn: (payload: UpdateCompanyInfoPayload) =>
      companyApi.updateCompanyInfo(companySeq, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setEditSection(null);
      setActionMessage('매장 정보를 수정했습니다.');
    },
    onError: () => setActionMessage('매장 정보 수정에 실패했습니다.'),
  });
  // 테스트 매장 지정/해제 — 실사용자 발견 목록에서 숨김(테스트 계정에는 계속 노출)
  const toggleTestMutation = useMutation({
    mutationFn: (testYn: 'Y' | 'N') =>
      companyApi.updateCompanyInfo(companySeq, { testYn }),
    onSuccess: (_data, testYn) => {
      queryClient.invalidateQueries({
        queryKey: ['companyDetail', String(companySeq)],
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setActionMessage(
        testYn === 'Y'
          ? '테스트 매장으로 지정했습니다.'
          : '테스트 매장 지정을 해제했습니다.'
      );
    },
    onError: () => setActionMessage('테스트 매장 설정 변경에 실패했습니다.'),
  });
  const submitEdit = () => {
    let payload: UpdateCompanyInfoPayload = {};
    if (editSection === 'basic') {
      payload = {
        mainTelNumber: editForm.mainTelNumber,
        zipcode: editForm.zipcode,
        companyAddr: editForm.companyAddr,
        companyAddrDetail: editForm.companyAddrDetail,
        region: editForm.region,
        playgroundYn: editForm.playgroundYn,
        maxWeight:
          editForm.maxWeight === '' ? null : Number(editForm.maxWeight),
        duplicateReservYn: editForm.duplicateReservYn,
        reservCapacityLimitYn: editForm.reservCapacityLimitYn,
        dailyReservCapacity:
          editForm.reservCapacityLimitYn !== 'Y' ||
          editForm.dailyReservCapacity === ''
            ? null
            : Number(editForm.dailyReservCapacity),
      };
    } else if (editSection === 'settlement') {
      payload = {
        mainBank: editForm.mainBank,
        mainAccountNumber: editForm.mainAccountNumber,
        depositor: editForm.depositor,
      };
    } else if (editSection === 'operation') {
      payload = {
        kakaoAlimUseYn: editForm.kakaoAlimUseYn,
        kakaoAlimFallbackSmsYn: editForm.kakaoAlimFallbackSmsYn,
        remainTicketIssueQty:
          editForm.remainTicketIssueQty === ''
            ? null
            : Number(editForm.remainTicketIssueQty),
      };
    }
    updateInfoMutation.mutate(payload);
  };

  const handleApprove = () => updateActiveMutation.mutate({ activeYn: 'Y' });

  const handleDeny = () => {
    const trimmed = deniedReason.trim();
    if (!trimmed) {
      setActionMessage('거절 사유를 입력해주세요.');
      return;
    }
    updateActiveMutation.mutate({ activeYn: 'N', deniedReason: trimmed });
  };

  const openDirectModal = (activeYn: 'Y' | 'N') => {
    setNotifyOnActivate(true);
    setDirectTarget(activeYn);
  };

  const members = company?.members ?? [];

  const memberStats = useMemo(() => {
    const owners = members.filter(
      m => m.cdUserType === 'USER_TYPE_OWNER'
    ).length;
    const teachers = members.filter(
      m => m.cdUserType === 'USER_TYPE_TEACHER'
    ).length;
    return { owners, teachers };
  }, [members]);

  // 우측 패널(7/12) 폭에 맞춰 목록 페이지보다 컬럼을 좁게 잡는다
  const memberColumns = useMemo<ColDef<CompanyMemberDto>[]>(
    () => [
      { headerName: '이름', field: 'bizUserName', width: 96 },
      {
        headerName: '구분',
        field: 'cdUserType',
        width: 84,
        cellRenderer: makeBadgeCell({
          toneMap: { USER_TYPE_OWNER: 'primary', USER_TYPE_TEACHER: 'info' },
          labelMap: { USER_TYPE_OWNER: '사업자', USER_TYPE_TEACHER: '선생님' },
        }),
      },
      { headerName: '이메일', field: 'email', flex: 1, minWidth: 130 },
      { headerName: '전화번호', field: 'phoneNumber', width: 118 },
      {
        headerName: '가입일',
        field: 'createdAt',
        width: 96,
        valueFormatter: p => formatDate(p.value as string),
      },
      {
        headerName: '상태',
        field: 'status',
        width: 76,
        cellRenderer: makeBadgeCell({
          toneMap: { Y: 'success', N: 'neutral' },
          labelMap: { Y: '활성', N: '비활성' },
        }),
      },
      {
        headerName: '관리',
        width: 100,
        sortable: false,
        cellRenderer: makeActionCell<CompanyMemberDto>([
          {
            label: 'PIN 초기화',
            variant: 'danger',
            onClick: row => setPinResetTarget(row),
          },
        ]),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <LoadingState message="매장 정보를 불러오는 중..." />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <ErrorState
          error={error ?? '매장 정보를 찾을 수 없습니다.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const fullAddress =
    `${company.companyAddr ?? ''} ${company.companyAddrDetail ?? ''}`.trim() ||
    '-';

  const isRequested = company.activationRequestStatus === 'REQUESTED';
  const isActive = company.activeYn === 'Y';

  // 크레딧 차감 면제 — 종료 일시가 현재보다 미래면 적용 중. 남은 일수(당일 포함 올림)를 함께 계산.
  const exemptActive =
    !!company.exemptUntil && new Date(company.exemptUntil) > new Date();
  const exemptDaysLeft = exemptActive
    ? Math.max(
        1,
        Math.ceil(
          (new Date(company.exemptUntil!).getTime() - Date.now()) / 86400000
        )
      )
    : 0;
  const exemptUntilText = company.exemptUntil
    ? formatDate(company.exemptUntil)
    : '';

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border-2 border-primary-200 bg-white">
      {/* ── 헤더: 매장 요약 + 활성화 액션 ── */}
      <div className="shrink-0 border-b border-gray-200 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-bold text-gray-900">
                {company.companyName}
              </h2>
              <span className="text-xs font-normal text-gray-500">
                SEQ {company.companySeq}
              </span>
              <StatusBadge
                tone={company.activeYn === 'Y' ? 'success' : 'neutral'}
              >
                {company.activeYn === 'Y' ? '활성' : '비활성'}
              </StatusBadge>
              {company.testYn === 'Y' && (
                <StatusBadge tone="danger">TEST</StatusBadge>
              )}
              {shouldShowActivationBadge(
                company.activeYn,
                company.activationRequestStatus
              ) && (
                <StatusBadge
                  tone={
                    company.activationRequestStatus === 'AGREED'
                      ? 'primary'
                      : company.activationRequestStatus === 'REQUESTED'
                        ? 'warning'
                        : company.activationRequestStatus === 'DENIED'
                          ? 'danger'
                          : 'neutral'
                  }
                >
                  {ACTIVATION_TEXT[company.activationRequestStatus ?? ''] ??
                    '-'}
                </StatusBadge>
              )}
              {exemptActive && (
                <span
                  title={`크레딧 차감 면제 · ${exemptUntilText}까지${
                    company.exemptReason ? ` · ${company.exemptReason}` : ''
                  }`}
                >
                  <StatusBadge tone="info">
                    🛡 면제 D-{exemptDaysLeft}
                  </StatusBadge>
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {company.region || '-'} · {fullAddress}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* 활성화 요청이 올라온 매장은 기존대로 승인/거절로 처리한다 */}
            {isRequested && (
              <>
                <Button
                  size="sm"
                  onClick={() => setIsApproveModalOpen(true)}
                  loading={updateActiveMutation.isPending}
                >
                  승인
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setIsDenyModalOpen(true)}
                  loading={updateActiveMutation.isPending}
                >
                  거절
                </Button>
              </>
            )}
            {/* 요청이 없어도 현재 상태에 따라 직접 켜고 끌 수 있다.
                요청 대기 중인 매장은 승인 버튼이 활성화를 겸하므로 '활성화'는 숨긴다 */}
            {isActive ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openDirectModal('N')}
                loading={activationStateMutation.isPending}
              >
                비활성화
              </Button>
            ) : (
              !isRequested && (
                <Button
                  size="sm"
                  onClick={() => openDirectModal('Y')}
                  loading={activationStateMutation.isPending}
                >
                  활성화
                </Button>
              )
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-900"
              >
                닫기 ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── 탭 바 ── */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3">
        <div className="flex gap-1">
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
                {t.key === 'tickets' && activeTicketCount > 0 && (
                  <span className="ml-1 text-xs text-gray-400">
                    {activeTicketCount}
                  </span>
                )}
                {t.key === 'members' && members.length > 0 && (
                  <span className="ml-1 text-xs text-gray-400">
                    {members.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 탭 본문 (내부 스크롤) ── */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
        {tab === 'basic' && (
          <>
            <Section
              title="매장 정보"
              right={
                <button
                  type="button"
                  onClick={() => openEdit('basic')}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600"
                >
                  수정
                </button>
              }
            >
              <DescList
                cols={2}
                items={[
                  { label: '매장명', value: company.companyName },
                  { label: '대표번호', value: company.mainTelNumber || '-' },
                  { label: '우편번호', value: company.zipcode || '-' },
                  { label: '주소', value: fullAddress },
                  { label: '지역', value: company.region || '-' },
                  {
                    label: '좌표',
                    value:
                      company.latitude != null && company.longitude != null
                        ? `${company.latitude}, ${company.longitude}`
                        : '-',
                  },
                  {
                    label: '실외 운동장',
                    value: company.playgroundYn === 'Y' ? '있음' : '없음',
                  },
                  {
                    label: '최대 수용 무게',
                    value:
                      company.maxWeight != null
                        ? `${company.maxWeight}kg`
                        : '-',
                  },
                  {
                    label: '중복예약 허용',
                    value: (
                      <StatusBadge
                        tone={
                          company.duplicateReservYn === 'Y'
                            ? 'success'
                            : 'neutral'
                        }
                      >
                        {company.duplicateReservYn === 'Y' ? '허용' : '차단'}
                      </StatusBadge>
                    ),
                  },
                  {
                    label: '예약 정원',
                    value:
                      company.reservCapacityLimitYn === 'Y' ? (
                        <StatusBadge tone="info">
                          {company.dailyReservCapacity != null
                            ? `제한 · 일 ${company.dailyReservCapacity}마리`
                            : '제한(미설정)'}
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="neutral">제한 없음</StatusBadge>
                      ),
                  },
                  { label: '등록일', value: formatDate(company.createdAt) },
                  {
                    label: '상태',
                    value: (
                      <StatusBadge
                        tone={company.activeYn === 'Y' ? 'success' : 'neutral'}
                      >
                        {company.activeYn === 'Y' ? '활성' : '비활성'}
                      </StatusBadge>
                    ),
                  },
                  {
                    label: '서비스 활성화',
                    value:
                      ACTIVATION_TEXT[company.activationRequestStatus ?? ''] ??
                      '-',
                  },
                  ...(company.activationRequestStatus === 'DENIED' &&
                  company.deniedReason
                    ? [{ label: '반려 사유', value: company.deniedReason }]
                    : []),
                  {
                    label: '크레딧 면제',
                    value: exemptActive ? (
                      <StatusBadge tone="info">
                        {`~${exemptUntilText} · ${exemptDaysLeft}일 남음`}
                        {company.exemptReason
                          ? ` · ${company.exemptReason}`
                          : ''}
                      </StatusBadge>
                    ) : company.exemptUntil ? (
                      <StatusBadge tone="neutral">{`만료됨 (~${exemptUntilText})`}</StatusBadge>
                    ) : (
                      <StatusBadge tone="neutral">없음</StatusBadge>
                    ),
                  },
                ]}
              />
            </Section>

            <Section title="사업자등록증">
              <DescList
                cols={1}
                items={[
                  {
                    label: '사업자등록번호',
                    value: company.businessNumber || '-',
                  },
                ]}
              />
              {company.businessFileSeq && company.businessFileUrl ? (
                <div className="mt-3">
                  <img
                    src={company.businessFileUrl}
                    alt="사업자등록증"
                    onClick={() => setIsImageViewerOpen(true)}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                    className="max-h-64 w-auto cursor-zoom-in rounded-md border border-gray-200"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    이미지를 클릭하면 원본 크기로 볼 수 있습니다.
                  </p>
                </div>
              ) : (
                <p className="mt-3 text-sm text-gray-400">
                  등록된 사업자등록증 이미지가 없습니다.
                </p>
              )}
            </Section>

            <Section
              title="매장 홍보사진"
              description={
                company.promoPhotos && company.promoPhotos.length > 0
                  ? `${company.promoPhotos.length}개 (대표 1개 · 지도·추천카드에 노출)`
                  : undefined
              }
            >
              {!company.promoPhotos || company.promoPhotos.length === 0 ? (
                <p className="text-sm text-gray-400">
                  등록된 홍보사진이 없습니다.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {company.promoPhotos.map(photo => {
                    const isVideo = photo.cdMediaType === 'VIDEO';
                    const thumb = photo.thumbnailUrl || photo.fileUrl || '';
                    const isRep = photo.representativeYn === 'Y';
                    return (
                      <div
                        key={photo.companyPhotoSeq}
                        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                      >
                        <div className="relative aspect-square bg-gray-100">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt="홍보사진"
                              className="h-full w-full object-cover"
                              onError={e => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                              미리보기 없음
                            </div>
                          )}
                          {isVideo && (
                            <span className="absolute inset-0 flex items-center justify-center bg-black/25 text-2xl text-white">
                              ▶
                            </span>
                          )}
                          {isRep && (
                            <span className="absolute left-1 top-1 rounded bg-primary-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              대표
                            </span>
                          )}
                        </div>
                        <div className="flex divide-x divide-gray-100 border-t border-gray-100 text-[11px]">
                          {!isRep && (
                            <button
                              type="button"
                              onClick={() =>
                                representPhotoMutation.mutate(
                                  photo.companyPhotoSeq
                                )
                              }
                              disabled={representPhotoMutation.isPending}
                              className="flex-1 py-1.5 text-gray-600 hover:bg-gray-50 hover:text-primary-600"
                            >
                              대표
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => downloadPhoto(photo)}
                            className="flex-1 py-1.5 text-gray-600 hover:bg-gray-50 hover:text-primary-600"
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            onClick={() => setPhotoDeleteTarget(photo)}
                            className="flex-1 py-1.5 text-red-500 hover:bg-red-50"
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section title="관리자 설정">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    테스트 매장
                    {company.testYn === 'Y' && (
                      <StatusBadge tone="danger">TEST</StatusBadge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    테스트로 지정하면 보호자 앱의 매장 검색·지도에는 테스트
                    계정에게만 노출됩니다.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={company.testYn === 'Y' ? 'danger' : 'outline'}
                  onClick={() =>
                    toggleTestMutation.mutate(
                      company.testYn === 'Y' ? 'N' : 'Y'
                    )
                  }
                  loading={toggleTestMutation.isPending}
                >
                  {company.testYn === 'Y' ? '테스트 해제' : '테스트 지정'}
                </Button>
              </div>
            </Section>
          </>
        )}

        {tab === 'operation' && (
          <>
            <Section
              title="운영 정보"
              right={
                <button
                  type="button"
                  onClick={() => openEdit('operation')}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600"
                >
                  수정
                </button>
              }
            >
              <DescList
                cols={2}
                items={[
                  {
                    label: '마지막 접속',
                    value: formatDateTime(company.dtLastAccess),
                  },
                  // 수동발급 잔여횟수는 승인 전 테스트 용도라 승인된 매장에는 표시하지 않음
                  ...(company.activationRequestStatus !== 'AGREED'
                    ? [
                        {
                          label: '테스트 발급 가능 횟수',
                          value:
                            company.remainTicketIssueQty != null
                              ? `${company.remainTicketIssueQty}회`
                              : '-',
                        },
                      ]
                    : []),
                  {
                    label: '알림톡 사용',
                    value: (
                      <StatusBadge
                        tone={
                          company.kakaoAlimUseYn === 'Y' ? 'success' : 'neutral'
                        }
                      >
                        {company.kakaoAlimUseYn === 'Y' ? '사용' : '미사용'}
                      </StatusBadge>
                    ),
                  },
                  {
                    label: 'SMS 대체발송',
                    value: (
                      <StatusBadge
                        tone={
                          company.kakaoAlimFallbackSmsYn === 'Y'
                            ? 'success'
                            : 'neutral'
                        }
                      >
                        {company.kakaoAlimFallbackSmsYn === 'Y'
                          ? '사용'
                          : '미사용'}
                      </StatusBadge>
                    ),
                  },
                ]}
              />
            </Section>

            <Section title="제공 서비스">
              <BadgeChips
                items={(company.provides ?? []).map((p, i) => ({
                  key: p.cdProvideType ?? `provide-${i}`,
                  label:
                    PROVIDE_LABEL[p.cdProvideType ?? ''] ??
                    p.provideName ??
                    '-',
                  active: p.activeYn === 'Y',
                }))}
                emptyMessage="등록된 제공 서비스가 없습니다."
              />
            </Section>

            <Section title="보유 시설">
              <BadgeChips
                items={(company.facilities ?? []).map((f, i) => ({
                  key: f.cdFacilityType ?? `facility-${i}`,
                  label:
                    FACILITY_LABEL[f.cdFacilityType ?? ''] ??
                    f.facilityName ??
                    '-',
                  active: f.activeYn === 'Y',
                }))}
                emptyMessage="등록된 시설이 없습니다."
              />
            </Section>

            <Section title="영업시간">
              {(company.businessTimes ?? []).length === 0 ? (
                <p className="text-sm text-gray-400">
                  등록된 영업시간이 없습니다.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {(company.businessTimes ?? []).map(t => (
                      <tr
                        key={t.dayOfWeek}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <td className="w-16 py-2 font-medium text-gray-700">
                          {DAY_OF_WEEK_LABEL[t.dayOfWeek] ?? t.dayOfWeek}
                        </td>
                        <td className="py-2">
                          {t.closedYn === 'Y' ? (
                            <StatusBadge tone="danger">휴무</StatusBadge>
                          ) : t.startTime && t.endTime ? (
                            <span className="text-gray-900">
                              {t.startTime} ~ {t.endTime}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          </>
        )}

        {tab === 'tickets' && (
          <Section
            title="등록 이용권 상품"
            description={
              tickets.length > 0
                ? `판매중 ${activeTicketCount}개 · 중지 ${tickets.length - activeTicketCount}개 · 누적 발급 ${tickets
                    .reduce((n, t) => n + (t.issuedCount ?? 0), 0)
                    .toLocaleString()}건`
                : undefined
            }
          >
            {ticketsQuery.isLoading ? (
              <LoadingState message="이용권 상품을 불러오는 중..." />
            ) : ticketsQuery.error ? (
              <ErrorState
                error={ticketsQuery.error}
                onRetry={() => ticketsQuery.refetch()}
              />
            ) : tickets.length === 0 ? (
              <EmptyState message="등록된 이용권 상품이 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {tickets.map(t => (
                  <CompanyTicketRow key={t.productTicketSeq} ticket={t} />
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === 'settlement' && (
          <Section
            title="정산 정보"
            right={
              <button
                type="button"
                onClick={() => openEdit('settlement')}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600"
              >
                수정
              </button>
            }
          >
            <DescList
              cols={2}
              items={[
                { label: '은행', value: company.mainBank || '-' },
                { label: '계좌번호', value: company.mainAccountNumber || '-' },
                { label: '예금주', value: company.depositor || '-' },
              ]}
            />
          </Section>
        )}

        {tab === 'members' && (
          <Section
            title="구성원"
            right={
              <span className="text-sm text-gray-500">
                사업자 {memberStats.owners}명 · 선생님 {memberStats.teachers}명
              </span>
            }
          >
            <DataGrid<CompanyMemberDto>
              columns={memberColumns}
              data={members}
              getRowId={row => String(row.bizUserSeq)}
              emptyMessage="소속된 사업자/선생님이 없습니다."
            />
          </Section>
        )}

        {tab === 'guardians' && (
          <CompanyGuardiansPanel companySeq={company.companySeq} />
        )}

        {tab === 'credit' && (
          <CompanyCreditPanel companySeq={company.companySeq} />
        )}

        {tab === 'storage' && (
          <CompanyStoragePanel companySeq={company.companySeq} />
        )}
      </div>

      {/* ── 모달 ── */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="매장 활성화 승인"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            해당 매장의 활성화 요청을 승인하시겠습니까?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsApproveModalOpen(false)}
              disabled={updateActiveMutation.isPending}
            >
              취소
            </Button>
            <Button
              onClick={handleApprove}
              loading={updateActiveMutation.isPending}
            >
              승인
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDenyModalOpen}
        onClose={() => setIsDenyModalOpen(false)}
        title="매장 활성화 거절"
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">거절 사유를 입력해주세요.</p>
          <textarea
            value={deniedReason}
            onChange={e => setDeniedReason(e.target.value)}
            placeholder="거절 사유를 입력하세요."
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDenyModalOpen(false)}
              disabled={updateActiveMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="danger"
              onClick={handleDeny}
              loading={updateActiveMutation.isPending}
            >
              거절
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={directTarget !== null}
        onClose={() => setDirectTarget(null)}
        title={directTarget === 'N' ? '매장 비활성화' : '매장 활성화'}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{company.companyName}</span> 매장을{' '}
            {directTarget === 'N' ? '비활성화' : '활성화'}하시겠습니까?
          </p>
          {/* 상태 변경 결과 설명 — 문구 확정 후 다시 노출
          {directTarget === 'N' ? (
            <p className="text-sm text-gray-500">
              비활성화하면 보호자에게 매장이 노출되지 않고 예약도 받을 수 없습니다. 활성화 상태는
              &apos;미요청&apos;으로 돌아가며, 사업주는 다시 활성화를 요청할 수 있습니다. 사업주에게
              가는 안내(메일·팝업)는 없습니다.
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              활성화 요청 없이 관리자가 직접 활성화합니다. 활성화 상태는 &apos;승인&apos;으로
              바뀌고, 이전 반려 사유는 삭제됩니다.
            </p>
          )}
          */}
          {directTarget === 'Y' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifyOnActivate}
                onChange={e => setNotifyOnActivate(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>사업주에게 활성화 안내 발송</span>
              {/* 대표자 이메일로 승인 안내 메일 + 사업자 웹 접속 시 안내 팝업 1회 (알림장 목록에는 미노출) */}
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDirectTarget(null)}
              disabled={activationStateMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant={directTarget === 'N' ? 'danger' : 'primary'}
              onClick={() =>
                directTarget &&
                activationStateMutation.mutate({
                  activeYn: directTarget,
                  notify: directTarget === 'Y' && notifyOnActivate,
                })
              }
              loading={activationStateMutation.isPending}
            >
              {directTarget === 'N' ? '비활성화' : '활성화'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!pinResetTarget}
        onClose={() => setPinResetTarget(null)}
        title="도장 PIN 초기화"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{pinResetTarget?.bizUserName}</span>{' '}
            님의 계약서 도장 PIN을 초기화하시겠습니까?
          </p>
          <p className="text-sm text-gray-500">
            기존 PIN이 삭제되고 실패 횟수/잠금이 해제됩니다. 초기화 후에는
            사업자가 직접 새 PIN을 등록해야 도장을 사용할 수 있습니다.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setPinResetTarget(null)}
              disabled={resetPinMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                pinResetTarget &&
                resetPinMutation.mutate(pinResetTarget.bizUserSeq)
              }
              loading={resetPinMutation.isPending}
            >
              초기화
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!photoDeleteTarget}
        onClose={() => setPhotoDeleteTarget(null)}
        title="홍보사진 삭제"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            이 홍보사진을 삭제하시겠습니까?
            {photoDeleteTarget?.representativeYn === 'Y' && (
              <span className="mt-1 block text-xs text-red-500">
                대표 사진입니다. 삭제 시 지도·추천카드의 대표 이미지 연결이
                해제됩니다.
              </span>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setPhotoDeleteTarget(null)}
              disabled={deletePhotoMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                photoDeleteTarget &&
                deletePhotoMutation.mutate(photoDeleteTarget.companyPhotoSeq)
              }
              loading={deletePhotoMutation.isPending}
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        title="사업자등록증"
        size="xl"
      >
        {company.businessFileUrl && (
          <div className="flex justify-center">
            <img
              src={company.businessFileUrl}
              alt="사업자등록증 원본"
              className="max-h-[70vh] w-auto"
            />
          </div>
        )}
      </Modal>

      {/* 매장 정보 수정 — 섹션별 */}
      <Modal
        isOpen={editSection !== null}
        onClose={() => setEditSection(null)}
        title={
          editSection === 'settlement'
            ? '정산 정보 수정'
            : editSection === 'operation'
              ? '운영 정보 수정'
              : '기본 정보 수정'
        }
        size="md"
      >
        <div className="space-y-3">
          {editSection === 'basic' && (
            <>
              <p className="text-xs text-gray-500">
                매장명·사업자번호는 수정할 수 없습니다.
              </p>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  대표번호
                </label>
                <input
                  type="text"
                  value={editForm.mainTelNumber}
                  placeholder="02-1234-5678"
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      mainTelNumber: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  주소
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editForm.zipcode}
                    readOnly
                    placeholder="우편번호"
                    className="w-28 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  />
                  <button
                    type="button"
                    onClick={openAddressSearch}
                    className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600"
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  type="text"
                  value={editForm.companyAddr}
                  readOnly
                  placeholder="주소 검색을 눌러 선택하세요"
                  className="mt-2 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                />
                {editForm.region && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    지역: {editForm.region} · 좌표는 저장 시 주소 기준으로 자동
                    갱신됩니다.
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  상세주소
                </label>
                <input
                  type="text"
                  value={editForm.companyAddrDetail}
                  placeholder="3층 301호"
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      companyAddrDetail: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    실외 운동장
                  </label>
                  <select
                    value={editForm.playgroundYn}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        playgroundYn: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="Y">있음</option>
                    <option value="N">없음</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    최대수용(kg)
                  </label>
                  <input
                    type="number"
                    value={editForm.maxWeight}
                    placeholder="-"
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        maxWeight: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    중복예약
                  </label>
                  <select
                    value={editForm.duplicateReservYn}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        duplicateReservYn: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="Y">허용</option>
                    <option value="N">차단</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    예약 정원
                  </label>
                  <select
                    value={editForm.reservCapacityLimitYn}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        reservCapacityLimitYn: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="N">제한 없음</option>
                    <option value="Y">제한</option>
                  </select>
                </div>
                {editForm.reservCapacityLimitYn === 'Y' && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      일일 정원(마리)
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={editForm.dailyReservCapacity}
                      placeholder="예: 20"
                      onChange={e =>
                        setEditForm(prev => ({
                          ...prev,
                          dailyReservCapacity: e.target.value,
                        }))
                      }
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {editSection === 'settlement' && (
            <>
              {(
                [
                  { key: 'mainBank', label: '은행', placeholder: '국민은행' },
                  {
                    key: 'mainAccountNumber',
                    label: '계좌번호',
                    placeholder: '123456-01-234567',
                  },
                  { key: 'depositor', label: '예금주', placeholder: '홍길동' },
                ] as const
              ).map(f => (
                <div key={f.key}>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    {f.label}
                  </label>
                  <input
                    type="text"
                    value={editForm[f.key]}
                    placeholder={f.placeholder}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        [f.key]: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              ))}
            </>
          )}

          {editSection === 'operation' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    알림톡 사용
                  </label>
                  <select
                    value={editForm.kakaoAlimUseYn}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        kakaoAlimUseYn: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="Y">사용</option>
                    <option value="N">미사용</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    SMS 대체발송
                  </label>
                  <select
                    value={editForm.kakaoAlimFallbackSmsYn}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        kakaoAlimFallbackSmsYn: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="Y">사용</option>
                    <option value="N">미사용</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  테스트 발급 가능 횟수
                </label>
                <input
                  type="number"
                  value={editForm.remainTicketIssueQty}
                  placeholder="-"
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      remainTicketIssueQty: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setEditSection(null)}
              disabled={updateInfoMutation.isPending}
            >
              취소
            </Button>
            <Button onClick={submitEdit} loading={updateInfoMutation.isPending}>
              저장
            </Button>
          </div>
        </div>
      </Modal>

      {actionMessage && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">
          {actionMessage}
        </div>
      )}
    </div>
  );
}
